/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import Fab from '@material-ui/core/Fab';
import MenuOpenIcon from '@material-ui/icons/MenuOpen';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import PaymentSummaryDrawerSection from './summary/PaymentSummaryDrawerSection';
import TopNavigation from '../../../layout/menu/TopNavigation';
import ApplicationInfoDrawerSection from './header/ApplicationInfoDrawerSection';

const useStylesDrawer = makeStyles(theme => ({
  paper: {
    width: '100%',
    padding: 0,
    backgroundColor: theme.palette.highlightsAndDividers,
    color: theme.palette.secondary.contrastText,
    zIndex: 1203,
    [theme.breakpoints.up('md')]: {
      width: 'calc(100% / 2)',
    },
    [theme.breakpoints.up('lg')]: {
      width: 'calc(100% / 3)',
    },
    [theme.breakpoints.up('xl2')]: {
      width: 'calc(100% / 4)',
    },
  },
}));

const useStyles = makeStyles(theme => ({
  widgetContainer: {
    color: theme.palette.secondary.contrastText,
    paddingTop: '1rem',
    paddingBottom: '1rem',
    paddingLeft: '2rem',
    paddingRight: '2rem',
  },
  widgetContainerDark: {
    color: theme.palette.secondary.contrastText,
    backgroundColor: '#E7EEF4',
  },
  title: {
    fontSize: '1.55rem',
    fontWeight: 600,
    marginBottom: '2.6rem',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1.5rem',
    [theme.breakpoints.up('md')]: {
      marginBottom: '2rem',
    },
  },
  fab: {
    backgroundColor: theme.palette[theme.fabColor].main,
    color: theme.palette[theme.fabColor].contrastText,
    '&[data-focustype="key"]': {
      outline: 'none',
      boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${theme.palette.focus}`,
    },
    '&:focus, &:active': {
      backgroundColor: theme.palette[theme.fabColor].active,
      outline: 'none',
    },
    '&:hover': {
      backgroundColor: theme.palette[theme.fabColor].hover,
    },
  },
  actionsWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '2rem',
    padding: '1.5rem 0',
  },
}));

export default function PaymentDrawer({
  user,
  currentOrganization,
  isDrawerOpen,
  toggleDrawer,
  submission,
  uiSchema,
  darkTheme = false,
}) {
  const classesDrawer = useStylesDrawer();
  const isLargeOrBigger = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const classes = useStyles();
  const { t } = useTranslation();

  const widgetStyle = clsx(classes.widgetContainer, { [classes.widgetContainerDark]: darkTheme });

  return (
    <Drawer
      classes={classesDrawer}
      anchor="right"
      open={isDrawerOpen}
      variant={isLargeOrBigger ? 'permanent' : 'temporary'}
      onClose={toggleDrawer}
    >
      {!isLargeOrBigger ? (
        <div className={classes.buttonsContainer}>
          <Fab size="medium" className={classes.fab} onClick={toggleDrawer}>
            <MenuOpenIcon />
          </Fab>
        </div>
      ) : (
        <div className={classes.actionsWrapper}>
          <TopNavigation user={user} currentOrganization={currentOrganization} />
        </div>
      )}
      <h1 className={clsx(classes.widgetContainer, classes.title)}>{t('submission.payment.summary')}</h1>
      <ApplicationInfoDrawerSection
        className={widgetStyle}
        basicFilingInfo={submission?.basicFilingInfo}
        darkTheme={false}
      />
      <PaymentSummaryDrawerSection className={widgetStyle} uiSchema={uiSchema} submission={submission} visible={true} />
    </Drawer>
  );
}
